import React, { useState, useEffect } from "react";
import { siteSettingsAPI } from "../services/api";
import toast from "react-hot-toast";

interface PaymentState {
  bankId: string;
  accountNumber: string;
  accountName: string;
}

interface BankOption {
  id: string;
  name: string;
}

const BANKS: BankOption[] = [
  { id: "VCB",  name: "Vietcombank" },
  { id: "TCB",  name: "Techcombank" },
  { id: "MBB",  name: "MB Bank" },
  { id: "ACB",  name: "ACB" },
  { id: "VPB",  name: "VPBank" },
  { id: "TPB",  name: "TPBank" },
  { id: "BIDV", name: "BIDV" },
  { id: "VTB",  name: "Vietinbank" },
  { id: "STB",  name: "Sacombank" },
  { id: "HDB",  name: "HDBank" },
  { id: "OCB",  name: "OCB" },
  { id: "MSB",  name: "MSB" },
  { id: "SHB",  name: "SHB" },
  { id: "SEAB", name: "SeABank" },
  { id: "NAB",  name: "NamABank" },
];

function PaymentSettingsPage(): React.ReactElement {
  const [payment, setPayment] = useState<PaymentState>({ bankId: "", accountNumber: "", accountName: "" });
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    siteSettingsAPI.getSettings().then((res) => {
      const data = res.data?.data;
      if (data?.payment) setPayment(data.payment as PaymentState);
    }).catch(() => {});
  }, []);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!payment.bankId || !payment.accountNumber || !payment.accountName) {
      toast.error("Vui lòng điền đầy đủ thông tin");
      return;
    }
    setSaving(true);
    try {
      await siteSettingsAPI.updatePayment(payment as unknown as Record<string, unknown>);
      toast.success("Đã lưu cài đặt thanh toán");
    } catch {
      toast.error("Lỗi khi lưu cài đặt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Thanh toán VietQR</h1>
        <p className="text-sm text-gray-500 mt-1">
          Thông tin tài khoản ngân hàng — hiển thị QR sau khi khách đặt phòng
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <form onSubmit={handleSave} className="space-y-4">
          {/* Ngân hàng */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ngân hàng *</label>
            <select
              value={payment.bankId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPayment({ ...payment, bankId: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500 bg-white text-sm"
              required
            >
              <option value="">-- Chọn ngân hàng --</option>
              {BANKS.map((b) => (
                <option key={b.id} value={b.id}>{b.id} — {b.name}</option>
              ))}
            </select>
          </div>

          {/* Số tài khoản */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Số tài khoản *</label>
            <input
              type="text"
              value={payment.accountNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPayment({ ...payment, accountNumber: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500 text-sm"
              placeholder="0123456789"
              required
            />
          </div>

          {/* Tên chủ TK */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tên chủ tài khoản *</label>
            <input
              type="text"
              value={payment.accountName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPayment({ ...payment, accountName: e.target.value.toUpperCase() })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coffee-500 text-sm uppercase"
              placeholder="NGUYEN VAN A"
              required
            />
            <p className="text-xs text-gray-400 mt-1">Nhập in hoa, đúng tên trên tài khoản ngân hàng</p>
          </div>

          {/* Preview QR */}
          {payment.bankId && payment.accountNumber && payment.accountName && (
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-xs text-gray-500 mb-3 font-medium">Preview QR mẫu (100.000đ):</p>
              <img
                src={`https://img.vietqr.io/image/${payment.bankId}-${payment.accountNumber}-compact2.png?amount=100000&addInfo=PREVIEW-TEST&accountName=${encodeURIComponent(payment.accountName)}`}
                alt="VietQR preview"
                className="h-48 mx-auto rounded-lg"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 bg-coffee-600 text-white rounded-lg font-medium hover:bg-coffee-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
            Lưu cài đặt thanh toán
          </button>
        </form>
      </div>
    </div>
  );
}

export default PaymentSettingsPage;
